/**
 * Enchanting table: bookshelf power, the three slot costs and vanilla's
 * enchantment selection (EnchantmentHelper.selectEnchantment), driven by a
 * port of java.util.Random so a given seed reproduces vanilla's rolls.
 *
 *   countBookshelves(getBlock, x, y, z)          0..15
 *   slotCosts(seed, bookshelves, itemId)         [top, mid, bottom] (0 = slot disabled)
 *   rollEnchantments(seed, slot, itemId, cost)   Ench[] the slot would apply
 */

import { BOOKSHELF_ID, IS_SOLID } from '../../blocks';
import { I } from '../itemIds';
import type { Ench } from '../types';
import { compatible, enchantability, ENCHANTS, maxCost, minCost, tableCanApply, type EnchDef } from './registry';

/** java.util.Random (48-bit LCG). */
export class JavaRandom {
  private seed: bigint;
  constructor(seed: number | bigint) { this.seed = (BigInt(seed) ^ 0x5DEECE66Dn) & ((1n << 48n) - 1n); }
  setSeed(seed: number | bigint): void { this.seed = (BigInt(seed) ^ 0x5DEECE66Dn) & ((1n << 48n) - 1n); }
  private next(bits: number): number {
    this.seed = (this.seed * 0x5DEECE66Dn + 0xBn) & ((1n << 48n) - 1n);
    const r = Number(this.seed >> BigInt(48 - bits));
    // to signed 32-bit
    return bits === 32 ? r | 0 : r;
  }
  nextInt(bound: number): number {
    if (bound <= 0) throw new Error('bound must be positive');
    if ((bound & -bound) === bound) return Number((BigInt(bound) * BigInt(this.next(31))) >> 31n);
    let bits: number, val: number;
    do {
      bits = this.next(31);
      val = bits % bound;
    } while (bits - val + (bound - 1) < 0 || bits - val + (bound - 1) > 0x7fffffff);
    return val;
  }
  nextFloat(): number { return this.next(24) / (1 << 24); }
}

/** Bookshelves around a table: ring at distance 2, y 0..1, with the cell between them empty (vanilla BOOKSHELF_OFFSETS). */
export function countBookshelves(getBlock: (x: number, y: number, z: number) => number, x: number, y: number, z: number): number {
  let n = 0;
  for (let dx = -2; dx <= 2; dx++) {
    for (let dz = -2; dz <= 2; dz++) {
      if (Math.abs(dx) !== 2 && Math.abs(dz) !== 2) continue;
      for (let dy = 0; dy <= 1; dy++) {
        if (getBlock(x + dx, y + dy, z + dz) !== BOOKSHELF_ID) continue;
        // the transmitter cell halfway in (Java integer division truncates toward 0)
        const mid = getBlock(x + Math.trunc(dx / 2), y + dy, z + Math.trunc(dz / 2));
        if (IS_SOLID[mid] === 0) n++;
      }
    }
  }
  return Math.min(15, n);
}

/** EnchantmentHelper.getEnchantmentCost for one slot (0..2). */
export function enchantmentCost(rand: JavaRandom, slot: number, bookshelves: number, itemId: number): number {
  if (enchantability(itemId) <= 0) return 0;
  const b = Math.min(15, bookshelves);
  const base = rand.nextInt(8) + 1 + (b >> 1) + rand.nextInt(b + 1);
  if (slot === 0) return Math.max(Math.floor(base / 3), 1);
  if (slot === 1) return Math.floor((base * 2) / 3) + 1;
  return Math.max(base, b * 2);
}

/** The three offered levels (vanilla EnchantmentMenu.slotsChanged). */
export function slotCosts(seed: number, bookshelves: number, itemId: number): [number, number, number] {
  const r = new JavaRandom(seed);
  const out: [number, number, number] = [0, 0, 0];
  for (let j = 0; j < 3; j++) {
    out[j] = enchantmentCost(r, j, bookshelves, itemId);
    if (out[j] < j + 1) out[j] = 0;
  }
  return out;
}

/** Every enchantment (at its best fitting level) available at modified level `level`. */
export function availableAt(level: number, itemId: number, treasure = false): Ench[] {
  const out: Ench[] = [];
  for (const e of ENCHANTS) {
    if (!e || (e.treasure && !treasure) || !tableCanApply(e, itemId)) continue;
    for (let l = e.max; l >= 1; l--) {
      if (level >= minCost(e, l) && level <= maxCost(e, l)) { out.push({ id: e.id, lvl: l }); break; }
    }
  }
  return out;
}

function weighted(rand: JavaRandom, list: Ench[]): Ench | null {
  let total = 0;
  for (const x of list) total += (ENCHANTS[x.id] as EnchDef).weight;
  if (total <= 0) return null;
  let r = rand.nextInt(total);
  for (const x of list) {
    r -= (ENCHANTS[x.id] as EnchDef).weight;
    if (r < 0) return x;
  }
  return null;
}

/** EnchantmentHelper.selectEnchantment. */
export function selectEnchantments(rand: JavaRandom, itemId: number, cost: number, treasure = false): Ench[] {
  const ench = enchantability(itemId);
  if (ench <= 0) return [];
  let level = cost + 1 + rand.nextInt(Math.floor(ench / 4) + 1) + rand.nextInt(Math.floor(ench / 4) + 1);
  const pct = (rand.nextFloat() + rand.nextFloat() - 1) * 0.15;
  level = Math.max(1, Math.round(level + level * pct));
  const out: Ench[] = [];
  let avail = availableAt(level, itemId, treasure);
  if (!avail.length) return out;
  const first = weighted(rand, avail);
  if (first) out.push(first);
  while (rand.nextInt(50) <= level) {
    const last = out[out.length - 1];
    avail = avail.filter((a) => compatible(a.id, last.id));
    if (!avail.length) break;
    const pick = weighted(rand, avail);
    if (pick) out.push(pick);
    level = Math.floor(level / 2);
  }
  return out;
}

/**
 * Everything the table shows (vanilla EnchantmentMenu.slotsChanged): one Random seeded
 * with the player's enchant seed rolls the three costs, then picks each slot's clue
 * from that slot's enchantment list. clue = -1 when the slot is empty.
 */
export function tableOffers(seed: number, bookshelves: number, itemId: number): { costs: [number, number, number]; clues: (Ench | null)[] } {
  const r = new JavaRandom(seed);
  const costs: [number, number, number] = [0, 0, 0];
  for (let j = 0; j < 3; j++) {
    costs[j] = enchantmentCost(r, j, bookshelves, itemId);
    if (costs[j] < j + 1) costs[j] = 0;
  }
  const clues: (Ench | null)[] = [null, null, null];
  for (let j = 0; j < 3; j++) {
    if (costs[j] <= 0) continue;
    const list = rollEnchantments(seed, j, itemId, costs[j]);
    if (list.length) clues[j] = list[r.nextInt(list.length)];
  }
  return { costs, clues };
}

/** What slot `slot` would put on the item (vanilla getEnchantmentList: seeded seed + slot; books lose one at random). */
export function rollEnchantments(seed: number, slot: number, itemId: number, cost: number): Ench[] {
  const r = new JavaRandom(seed + slot);
  const list = selectEnchantments(r, itemId, cost);
  if (itemId === I.BOOK && list.length > 1) list.splice(r.nextInt(list.length), 1);
  return list;
}
